import { BodyScrollEvent, GridApi } from 'ag-grid-community';
import { RefObject, useCallback, useEffect, useRef } from 'react';

import { SLIDING_PANEL_SCROLLABLE_CONTENT_SELECTOR } from 'core/components';

import { isFocusLocked as computeIsFocusLocked, willBodyScroll } from './focusLock';
import { COLLAPSE_FOCUS_LOCK_MS } from '../constants';

const DRAWER_CONTENT_SELECTOR = '.MuiDrawer-content';
// Inline-mode marker: the outer Stack in PartDetails(inline) opts in by setting
// data-partdetails-wheel-surface="true". Without this, the closest overflowY:auto
// ancestor is the Tabs-only inner Box — wheels on cards/breadcrumbs above the
// tabs never reach the handler, so the collapse never fires from that surface.
const WHEEL_SURFACE_SELECTOR = '[data-partdetails-wheel-surface="true"]';
const AG_GRID_BODY_VIEWPORT_SELECTOR = '.ag-body-viewport';
const FOCUS_LOCK_MS = COLLAPSE_FOCUS_LOCK_MS;

interface IUseScrollToFocusOptions {
  isFocused: boolean;
  onFocusChange?: (focused: boolean) => void;
  // AG-Grid tabs only: fallback row count used until the grid api is captured.
  getRowCount?: () => number;
  minRowsForTrigger?: number;
}

interface IUseScrollToFocusReturn {
  panelScrollAnchorRef: RefObject<HTMLDivElement>;
  onBodyScroll: (event: BodyScrollEvent) => void;
}

const findScrollableAncestor = (start: HTMLElement) => {
  let current: HTMLElement | null = start.parentElement;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);

    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }

  return null;
};

export const useScrollToFocus = ({
  isFocused,
  onFocusChange,
  getRowCount,
  minRowsForTrigger = 0,
}: IUseScrollToFocusOptions): IUseScrollToFocusReturn => {
  const panelScrollAnchorRef = useRef<HTMLDivElement>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const isFocusedRef = useRef(isFocused);
  const focusChangedAtRef = useRef(0);
  // Stamped only on upward wheel — multi-tick scroll-down would otherwise
  // unlock the rebound suppression after collapse.
  const lastUnfocusIntentAtRef = useRef(0);
  const getRowCountRef = useRef(getRowCount);

  useEffect(() => {
    isFocusedRef.current = isFocused;
  }, [isFocused]);

  useEffect(() => {
    getRowCountRef.current = getRowCount;
  }, [getRowCount]);

  const isFocusLocked = useCallback(
    () =>
      computeIsFocusLocked(
        focusChangedAtRef.current,
        lastUnfocusIntentAtRef.current,
        Date.now(),
        FOCUS_LOCK_MS
      ),
    []
  );

  const onBodyScroll = useCallback(
    (event: BodyScrollEvent) => {
      gridApiRef.current = event.api;

      if (!onFocusChange || event.direction !== 'vertical') {
        return;
      }

      if (isFocused && event.top <= 0) {
        if (isFocusLocked()) {
          return;
        }
        onFocusChange(false);

        return;
      }

      if (!isFocused && event.top > 0 && event.api.getDisplayedRowCount() > minRowsForTrigger) {
        focusChangedAtRef.current = Date.now();
        onFocusChange(true);
      }
    },
    [isFocused, onFocusChange, minRowsForTrigger, isFocusLocked]
  );

  useEffect(() => {
    if (!onFocusChange) {
      return;
    }

    const anchor = panelScrollAnchorRef.current;

    if (!anchor) {
      return;
    }

    let focusScroller: HTMLElement | null = null;
    let outerScroller: HTMLElement | null = null;
    let handleScroll: (() => void) | null = null;
    let handleWheel: ((event: WheelEvent) => void) | null = null;
    let previousScrollTop = 0;

    // Strict row-count gate when getRowCount is provided (mirrors the
    // Inventory MIN_ROWS_FOR_FOCUS_TRIGGER spec). Otherwise fall back to
    // scroller / AG body overflow for non-grid tabs.
    const meetsThreshold = (scroller: HTMLElement) => {
      const fallback = getRowCountRef.current;

      if (fallback) {
        const displayed = gridApiRef.current?.getDisplayedRowCount();
        const count = displayed ?? fallback();

        return count > minRowsForTrigger;
      }

      if (scroller.scrollHeight - scroller.clientHeight > 0) {
        return true;
      }

      const agBody = scroller.querySelector(AG_GRID_BODY_VIEWPORT_SELECTOR) as HTMLElement | null;

      return !!agBody && agBody.scrollHeight - agBody.clientHeight > 0;
    };

    const detach = () => {
      if (focusScroller && handleScroll) {
        focusScroller.removeEventListener('scroll', handleScroll);
      }
      if (outerScroller && handleWheel) {
        outerScroller.removeEventListener('wheel', handleWheel);
      }
      focusScroller = null;
      outerScroller = null;
      handleScroll = null;
      handleWheel = null;
    };

    const attach = () => {
      const nextOuter =
        anchor.closest<HTMLElement>(DRAWER_CONTENT_SELECTOR) ??
        anchor.closest<HTMLElement>(WHEEL_SURFACE_SELECTOR) ??
        findScrollableAncestor(anchor);
      const nextInner = anchor.querySelector<HTMLElement>(
        SLIDING_PANEL_SCROLLABLE_CONTENT_SELECTOR
      );
      const nextFocus = nextInner ?? nextOuter;

      if (nextFocus === focusScroller && nextOuter === outerScroller) {
        return;
      }

      detach();

      if (!nextFocus) {
        return;
      }

      focusScroller = nextFocus;
      outerScroller = nextOuter;
      previousScrollTop = nextFocus.scrollTop;

      handleScroll = () => {
        if (!focusScroller || !onFocusChange) {
          return;
        }
        const currentScrollTop = focusScroller.scrollTop;
        const scrolledDown = currentScrollTop > previousScrollTop;

        previousScrollTop = currentScrollTop;

        if (
          !isFocusedRef.current &&
          scrolledDown &&
          currentScrollTop > 0 &&
          meetsThreshold(focusScroller)
        ) {
          focusChangedAtRef.current = Date.now();
          onFocusChange(true);

          return;
        }

        if (isFocusedRef.current && currentScrollTop <= 0) {
          if (isFocusLocked()) {
            return;
          }
          onFocusChange(false);
        }
      };

      handleWheel = (event: WheelEvent) => {
        if (!focusScroller || !onFocusChange) {
          return;
        }
        if (event.deltaY < -1) {
          lastUnfocusIntentAtRef.current = Date.now();
        }

        const target = event.target as HTMLElement | null;
        const viewport = target?.closest<HTMLElement>(AG_GRID_BODY_VIEWPORT_SELECTOR);

        if (willBodyScroll(viewport, event.deltaY)) {
          return;
        }

        if (!isFocusedRef.current && event.deltaY > 1 && meetsThreshold(focusScroller)) {
          focusChangedAtRef.current = Date.now();
          onFocusChange(true);

          return;
        }

        if (isFocusedRef.current && event.deltaY < -1) {
          if (isFocusLocked()) {
            return;
          }
          onFocusChange(false);
        }
      };

      nextFocus.addEventListener('scroll', handleScroll, { passive: true });
      nextOuter?.addEventListener('wheel', handleWheel, { passive: true });
    };

    attach();

    // Re-discover scrollers when the anchor's direct children change (alt-version
    // toggle). Avoid subtree to skip noisy AG-Grid row mutations during scroll.
    const observer = new MutationObserver(() => attach());

    observer.observe(anchor, { childList: true });

    return () => {
      observer.disconnect();
      detach();
    };
  }, [onFocusChange, isFocusLocked, minRowsForTrigger]);

  // Sub-tabs remount on switch; if this hook mounts with the parent already
  // focused, reset to expanded once. Mount-only — later isFocused transitions
  // must not undo a user-initiated collapse.
  useEffect(() => {
    if (isFocusedRef.current && onFocusChange) {
      onFocusChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { panelScrollAnchorRef, onBodyScroll };
};
